import React, { useId, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

import completionMessages from '@common/backend/completionMessages';
import Description from '@common/components/Description';

function CompletionMessageModal() {
	const { t } = useTranslation();
	const titleId = useId();
	const bodyId = useId();
	const message = useSyncExternalStore(
		completionMessages.subscribe,
		completionMessages.getSnapshot,
		completionMessages.getSnapshot,
	);
	const bodyHtml = useMemo(
		() => DOMPurify.sanitize(message?.body_html ?? '', { USE_PROFILES: { html: true } }),
		[message?.body_html],
	);
	const buttonUrl = useMemo(() => {
		if (!message?.button_url.trim()) return null;
		try {
			const url = new URL(message.button_url, window.location.origin);
			return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
		} catch {
			return null;
		}
	}, [message?.button_url]);

	if (!message) return null;

	return (
		<Dialog
			open
			onClose={completionMessages.close}
			aria-labelledby={titleId}
			aria-describedby={bodyId}
			PaperProps={{ sx: { maxWidth: '560px' } }}
		>
			<DialogTitle
				component="div"
				sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 0, pb: 2 }}
			>
				<Typography id={titleId} component="h2" variant="h3" sx={{ flex: 1, overflowWrap: 'anywhere' }}>
					{message.title}
				</Typography>
				<IconButton aria-label={t('shared.close')} onClick={completionMessages.close}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent
				id={bodyId}
				sx={{
					p: 0,
					overflowY: 'auto',
					overflowWrap: 'anywhere',
					'& > .ck-content': { p: 0 },
					'& p + p': { mt: 2 },
				}}
			>
				<Description html={bodyHtml} />
			</DialogContent>
			{buttonUrl && (
				<DialogActions>
					<Button
						variant="contained"
						href={buttonUrl}
						target="_blank"
						rel="noopener noreferrer"
					>
						{t('shared.continue')}
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
}

export default CompletionMessageModal;
